/**
 * localStorage와 app.json5 간의 언어 설정 동기화를 담당하는 컴포넌트
 * TrpcProvider 내부에서만 사용 가능 (tRPC 필요)
 *
 * 동작:
 * 1. 앱 로드 시: app.json5 → localStorage (최초 1회)
 * 2. 언어 변경 시: localStorage → app.json5 (즉시)
 */

import { useEffect, useRef } from 'react';
import { trpc } from '../common/trpc';
import type { SupportedLocale } from '../locales/types';

export function LocaleSyncManager() {
  const { data: appInfo } = trpc.app.info.useQuery();
  const updateLocaleMutation = trpc.app.updateLocale.useMutation();
  const initialSyncDone = useRef(false);

  // 1. 초기 로드: app.json5 → localStorage (최초 1회만)
  useEffect(() => {
    if (!initialSyncDone.current && appInfo?.config?.locale) {
      const serverLocale = appInfo.config.locale;
      const localStorageLocale = localStorage.getItem('locale');

      // localStorage에 값이 없거나 서버와 다르면 서버 값으로 동기화
      if (!localStorageLocale || localStorageLocale !== `"${serverLocale}"`) {
        localStorage.setItem('locale', JSON.stringify(serverLocale));
        // 페이지 새로고침하여 LocaleProvider가 새 값을 읽도록 함
        window.dispatchEvent(new Event('storage'));
      }

      initialSyncDone.current = true;
    }
  }, [appInfo?.config?.locale]);

  // 2. localStorage 변경 감지 → app.json5 (양방향 동기화)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'locale' && e.newValue) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- JSON.parse returns unknown, narrowing to SupportedLocale
          const newLocale = JSON.parse(e.newValue) as SupportedLocale;

          console.log(
            `handleStorageChange: new locale = ${newLocale}, appInfo?.config?.locale = ${appInfo?.config?.locale}`,
          );
          // app.json5와 다르면 업데이트
          if (appInfo?.config?.locale !== newLocale) {
            updateLocaleMutation.mutate({ locale: newLocale });
          }
        } catch (error) {
          console.error('Failed to parse locale from localStorage:', error);
        }
      }
    };

    // storage 이벤트 리스너 (다른 탭/창에서의 변경 감지)
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [appInfo?.config?.locale, updateLocaleMutation]);

  // 이 컴포넌트는 UI를 렌더링하지 않습니다
  return null;
}
